import { ref, onBeforeUnmount, Ref } from "vue"
// @ts-ignore
import placeholderImage from "../images/v-placeholder.jpg"

export interface LazyImageOptions {
	placeholder?: string
	rootMargin?: string
	threshold?: number
}

export function useLazyImage(options: LazyImageOptions = {}) {
	const { placeholder = placeholderImage, rootMargin = "10px", threshold = 0.5 } = options

	const imageRef: Ref<HTMLImageElement | null> = ref(null)
	const isLoaded = ref(false)
	const currentSrc = ref(placeholder)
	let observer: IntersectionObserver | null = null

	const loadImage = (src: string) => {
		if (!src || isLoaded.value) return

		const img = new Image()
		img.onload = () => {
			currentSrc.value = src
			isLoaded.value = true
			if (observer && imageRef.value) {
				observer.unobserve(imageRef.value)
			}
		}
		img.onerror = () => {
			console.warn("Failed to load image:", src)
		}
		img.src = src
	}

	const observe = (actualSrc: string) => {
		if (!imageRef.value || !actualSrc) return

		// If IntersectionObserver is not supported, load immediately
		if (!("IntersectionObserver" in window)) {
			loadImage(actualSrc)
			return
		}

		observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						loadImage(actualSrc)
					}
				})
			},
			{
				rootMargin,
				threshold,
			}
		)

		observer.observe(imageRef.value)
	}

	onBeforeUnmount(() => {
		if (observer && imageRef.value) {
			observer.unobserve(imageRef.value)
			observer.disconnect()
		}
	})

	return {
		imageRef,
		currentSrc,
		isLoaded,
		observe,
	}
}
